import { useContext, useEffect, useState } from 'react'

import {
  CryptographyWrapper,
  useCryptographyStore,
} from '@builddocs/cryptography-js'
import {
  getPoaDataToSign,
  useCheckOperation,
  PoaItem,
  useGetMRFList,
  User,
  useVersionOf,
} from '@builddocs/data-management'
import {
  FaceControlContext,
  SignHandlerParams,
} from '@builddocs/feature-faceid'
// import { DeviationDialog } from "@builddocs/mui"
import {
  getDigestFinalRegistry,
  getDigestRegistry,
  SignatureState,
  signFinalRegistry,
  signRegistry,
} from '@builddocs/filestore-api'
import { useToast } from '@builddocs/system'
import { Button, Stack } from '@mui/material'
import format from 'date-fns/format'

import { sendSignedRegistry } from '../Utils/sign-registry'
import { prepareHash } from '../Utils/sign-registry/prepare-hash'

type OwnProps = {
  registry: any
  user: User
  type?: 'default' | 'final'
}

export const SignRegistryPanel = ({
  registry,
  user,
  type = 'default',
}: OwnProps) => {
  const { cryptography_version, poaIsEnabled } = useVersionOf()

  const faceControl = useContext(FaceControlContext)
  const { toast } = useToast()
  const [proxy, setProxy] = useState<PoaItem | null>(null)
  const { data: proxyList } = useGetMRFList({
    userId: user.userId,
    enabled: poaIsEnabled,
  })

  const handleSign = () => {
    // Открываем диалог верификации и передаём коллбэк для подписи
    faceControl.openFaceControl({
      isEmbedded: true,
      getDigestHandler: async ({
        signedHash,
        keyIssuer,
        imprint,
        facePhoto,
        signerName,
        keySerialNumber,
        certificateDateStart,
        certificateDateEnd,
        withPoa,
        poaId,
        poaDateStart,
        poaDateEnd,
      }) => {
        const poa = proxyList?.poas?.find(poa => poa?.poaIdInternal === poaId)
        const poaData = getPoaDataToSign({
          proxyId: poaId,
          poa,
          poaIsEnabled,
          withPoa,
          poaDateStart,
          poaDateEnd,
        })

        setProxy(poa ?? null)
        const args = {
          base64signature: signedHash,
          documentId: registry.id,
          employmentId: user.employment.employmentId,
          imprint: imprint,
          keyIssuer: keyIssuer,
          signatureState: SignatureState.PENDING as SignatureState.PENDING,
          certificateDateStart,
          certificateDateEnd,
          name: signerName,
          faceId: {
            faceImage: facePhoto,
            keySerialNumber,
          },
          ...poaData,
        }

        const aosrDigestResponse =
          type === 'default'
            ? await getDigestRegistry(args)
            : await getDigestFinalRegistry(args)

        return aosrDigestResponse
      },
      signHandler: async ({
        signedHash,
        actRegistryBase64signature,
        keyIssuer,
        imprint,
        facePhoto,
        keySerialNumber,
        signerName,
        withPoa,
        poaId,
        poaDateStart,
        poaDateEnd,
      }: SignHandlerParams) => {
        const poa = proxyList?.poas?.find(poa => poa?.poaIdInternal === poaId)
        const poaData = getPoaDataToSign({
          proxyId: poaId,
          poa,
          poaIsEnabled,
          withPoa,
          poaDateStart,
          poaDateEnd,
        })

        setProxy(poa ?? null)
        const args = {
          documentId: registry.id,
          employmentId: user.employment.employmentId,
          signatureState: SignatureState.SIGNED as SignatureState.SIGNED,
          base64signature: signedHash,
          actRegistryBase64signature,
          keyIssuer,
          imprint,
          name: signerName,
          faceId: {
            faceImage: facePhoto,
            keySerialNumber,
          },
          ...poaData,
        }

        return type === 'default'
          ? await signRegistry(args)
          : await signFinalRegistry(args)
      },
      onSuccess: res => {
        if (res.status === 200) {
          //   refreshTasksOfDocument()
          //   refreshTaskSigningStamps()
          toast.success('Документ подписан')
          //   onUpdatePDF && onUpdatePDF()
        }
        console.debug('Получен дайджест документа')
      },
      onFailure: error => {
        if (error.response && error.response.status === 401) {
          toast.error(`Не удалось подтвердить вашу личность!`)
        } else {
          toast.error(
            `Не удалось подписать! Ошибка ${
              error.response?.status ||
              'при подписании данным ключом. Удостоверьтесь, что вы не использовали его ранее для этого реестра'
            }`,
          )
        }
      },
    })
  }

  // 2.0 cryptography business logic
  const { selectedCertificate } = useCryptographyStore()

  const [isGeneratingSignContent, setIsGeneratingSignContent] = useState(false)

  useEffect(() => {
    if (selectedCertificate) {
      prepareHashForSigning()
    }
  }, [selectedCertificate])

  const prepareHashForSigning = async () => {
    if (!selectedCertificate) return undefined
    const poaData = getPoaDataToSign({
      proxyId: proxy?.poaIdInternal,
      poa: proxy,
      poaIsEnabled,
    })

    try {
      setIsGeneratingSignContent(true)
      const result = await prepareHash({
        documentId: registry.id,
        employmentId: user.employment.employmentId,
        imprint: selectedCertificate.Thumbprint,
        keyIssuer: selectedCertificate.Issuer.CN,
        certificateDateStart: format(
          selectedCertificate.ValidFromDate,
          'yyyy-MM-dd',
        ),
        certificateDateEnd: format(
          selectedCertificate.ValidToDate,
          'yyyy-MM-dd',
        ),
        signerName: selectedCertificate.Name,
        facePhoto: '',
        keySerialNumber: selectedCertificate.SerialNumber,
        type: type,
        ...poaData,
      })

      setIsGeneratingSignContent(false)
      return result
    } catch (error) {
      console.error('Error preparing data:', error)
      toast.error('Ошибка при подготовке данных для подписания')
      setIsGeneratingSignContent(false)
      return undefined
    }
  }

  const sendSignedData = async (hash: string, proxyId: string | null) => {
    const poa = proxyList?.poas?.find(poa => poa?.poaIdInternal === proxyId)
    const poaData = getPoaDataToSign({
      proxyId,
      poa,
      poaIsEnabled,
    })

    if (!selectedCertificate) return
    setIsGeneratingSignContent(true)
    return await sendSignedRegistry({
      signedHash: hash,
      actRegistryBase64signature: '',
      documentId: registry.id,
      employmentId: user.employment.employmentId,
      imprint: selectedCertificate.Thumbprint,
      keyIssuer: selectedCertificate.Issuer.CN,
      signerName: selectedCertificate.Name,
      facePhoto: '',
      keySerialNumber: selectedCertificate.SerialNumber,
      type: type,
      ...poaData,
    })
      .then(() => {
        toast.success('Документ подписан')
        //   onUpdatePDF && onUpdatePDF()
        setIsGeneratingSignContent(false)
      })
      .catch(() => {
        toast.error('Ошибка при подписании документа')
        setIsGeneratingSignContent(false)
      })
  }

  // 2.0 cryptography business logic
  const { checkOperation } = useCheckOperation()
  const handleSignClick = () => {
    checkOperation({
      operationId:
        type === 'default'
          ? 'SIGN_CLOSING_REGISTRY'
          : 'SIGN_SECTION_CLOSING_REGISTRY',
      operationCallback: () => {
        handleSign()
      },
    })
  }

  return (
    <Stack direction="row" justifyContent="center" spacing={4}>
      {cryptography_version === 'v1' && (
        <Button onClick={handleSignClick}>Подписать реестр</Button>
      )}
      {/* <DeviationDialog
        additionalInfo={{ id: docId }}
        documentType={Application.REGISTRY}
        title="Отклонение штампа"
        onSubmit={(...args) => {
          onRejectStamp(args)
        }}
      /> */}

      {cryptography_version === 'v2' && (
        <CryptographyWrapper
          currentUser={{
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            surname: '',
            middleName: user?.middleName || '',
            userId: user?.userId || '',
          }}
          forceDisableSignButton={isGeneratingSignContent}
          proxy={proxyList?.poas}
          proxyIsEnabled={poaIsEnabled}
          signContent={prepareHashForSigning as () => Promise<string>}
          signType="string"
          onSuccess={sendSignedData}
        >
          {({ onOpen }) => (
            <Button
              key={2}
              color="accepted"
              disabled={isGeneratingSignContent}
              size="small"
              onClick={onOpen}
            >
              Подписать реестр
            </Button>
          )}
        </CryptographyWrapper>
      )}
    </Stack>
  )
}
